import {observable, action, computed, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {IAuthService} from '../../services/auth/auth.service';
import {AppState} from '../../app.state';
import {TsMultiSelectController} from '../../components/ts-multi-select/ts-multi-select.controller';
import {ChangeAccountModalState, Account} from './ChangeAccountModal.state';
import get from 'lodash/get';
import find from 'lodash/find';
import {getRootStore} from '../../app.bootstrap';

interface ChangeAccountModalOptions {
    currentUser: any;
    accountData: any;
}

export class ChangeAccountModalController extends TechseeBaseController {
    private currentUser: any;
    private accountId: string = '';
    private parentSubUserId: string | undefined;
    private resolvePromise: ((result?: {reload: boolean}) => void) | null = null;
    private rejectPromise: (() => void) | null = null;
    multiSelectController: TsMultiSelectController | null = null;

    constructor(
        public state: ChangeAccountModalState,
        private authService: IAuthService,
        private appState: AppState
    ) {
        super();
    }

    @action
    open(opts: ChangeAccountModalOptions): Promise<{reload: boolean} | void> {
        this.currentUser = opts.currentUser;
        this.accountId = opts.currentUser.accountId;
        this.parentSubUserId = get(opts.currentUser, 'parentSubUserId');
        this.state.selectedAccountId = null;
        this.state.accounts = [];
        this.state.loading = true;
        this.state.isVisible = true;

        // Initialize multi-select controller
        const useNewInterface = (this as any).useNewInterface || false;
        this.multiSelectController = new TsMultiSelectController(useNewInterface, {
            id: 'multi-user',
            inputId: 'big-label',
            controlName: 'multi-user',
            inputModel: this.state.accounts,
            outputModel: this.state.selectedAccountId,
            selectedProperty: 'selected',
            outputProperties: ['_id'],
            buttonLabel: ['companyName'],
            itemLabel: ['companyName'],
            placeholder: 'SelectAccount',
            onChange: this.onAccountChange
        });

        this._loadAccounts();

        return new Promise<{reload: boolean} | void>((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
    }

    @action
    close(result?: {reload: boolean}) {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;
        this.state.loading = false;
        this.state.saving = false;
        this.state.accounts = [];
        this.state.selectedAccountId = null;
        this.multiSelectController = null;

        if (this.resolvePromise) {
            this.resolvePromise(result);
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        this.emit('CLOSED');
    }

    @action
    dismiss() {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;
        this.state.loading = false;
        this.state.saving = false;
        this.state.accounts = [];
        this.state.selectedAccountId = null;
        this.multiSelectController = null;

        if (this.resolvePromise) {
            this.resolvePromise(undefined);
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        this.emit('DISMISSED');
    }

    @action
    onAccountChange = (account: Account) => {
        const accountId = account._id;
        this.state.selectedAccountId = accountId;
        this.appState.setSelectedMultiAccount(accountId);
    };

    @action
    async submit() {
        if (!this.state.selectedAccountId || this.state.selectedAccountId === this.accountId) {
            return this.dismiss();
        }

        runInAction(() => {
            this.state.saving = true;
        });

        try {
            await this.authService.loginSubUser(this.state.selectedAccountId);
            this.close({reload: true});
        } catch (error) {
            runInAction(() => {
                this.state.saving = false;
            });
            throw error;
        }
    }

    private async _loadAccounts() {
        const userIdToFetch = this.parentSubUserId || this.currentUser._id;

        if (!userIdToFetch) {
            runInAction(() => {
                this.state.loading = false;
            });
            return;
        }

        try {
            const user = await getRootStore().userService.getUser(userIdToFetch);
            const accounts = ((user as any).accounts || []) as Account[];

            runInAction(() => {
                this.state.accounts = accounts;
                this.state.loading = false;

                // Mark current account as selected
                if (this.state.accounts.length > 0) {
                    const selectedAccount = find(this.state.accounts, (account) => account._id === this.accountId);
                    if (selectedAccount) {
                        selectedAccount.selected = true;
                        this.state.selectedAccountId = this.accountId;
                        this.appState.setSelectedMultiAccount(this.accountId);
                    }
                }

                // Update multi-select controller input model
                if (this.multiSelectController) {
                    this.multiSelectController.updateConfig({inputModel: this.state.accounts});
                }
            });
        } catch (error) {
            runInAction(() => {
                this.state.loading = false;
            });
            throw error;
        }
    }

    @computed
    get canSubmit() {
        return this.state.selectedAccountId && this.state.selectedAccountId !== this.accountId && !this.state.saving;
    }
}
